<template>
  <div class="tools02-promotions-promotion-bulk-editor">
    <p class="description">
      承認率・報酬額を一括編集できます。<br />
      ・Excel やスプレッドシートから範囲コピーしてセルに貼り付け（コピペ）<br />
      ・セルを選択し、選択範囲の右下をドラッグで連続入力（オートフィル）<br />
      ※ ASP名・プログラム名・ASPプログラム名・プログラムID・オフラインCV名 は目印用で編集できません。<br />
      ※ 報酬額の入力が不要な ASP（CSVから報酬額が取得できる ASP）の行は、グレーで編集できません。
    </p>
    <el-row class="search-row">
      <el-col :span="8">
        <el-input v-model="searchText" placeholder="絞り込み検索（ASP名・プログラム名など）" clearable></el-input>
      </el-col>
    </el-row>
    <revo-grid ref="grid" class="grid" theme="material"></revo-grid>
    <div class="actions">
      <el-button type="primary" @click="update" :loading="isUpdating">保存</el-button>
      <el-button @click="$emit('cancel')">キャンセル</el-button>
    </div>

    <div v-show="tooltip.visible" class="cell-error-tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
      {{ tooltip.message }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Tools02PromotionsPromotionBulkEditor",
  props: {
    promotionList: Array,
  },
  data() {
    return {
      isUpdating: false,
      searchText: "",
      allRows: [],
      // 変更セルの色付け用：行の元の値（promotion_id => {approvalRate, reward}）
      originalByPromotionId: {},
      // エラーセルの赤ハイライト＋ツールチップ用（promotion_id => {field => message}）
      errorByPromotionId: {},
      // 即時表示の自前ツールチップ（エラーアイコンのホバー用）
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        message: "",
      },
    };
  },
  computed: {
    ...mapGetters(["rt", "apiErrorMessage"]),
  },
  watch: {
    searchText: {
      handler() {
        this.applyFilter();
      },
    },
  },
  mounted() {
    const grid = this.$refs.grid;
    if (grid === undefined) {
      console.log("revo-grid 要素が取得できませんでした。RevoGrid の登録を確認してください。");
      return;
    }

    //
    console.log("revo-grid を初期化します。");
    this.allRows = this.buildRows();
    grid.columns = this.buildColumns();
    grid.source = this.allRows;
    grid.range = true;
    grid.resize = true;
    // Enter を押さなくても、セルからフォーカスが外れた時点で入力を確定する（スプレッドシート的な挙動）
    grid.applyOnClose = true;
    // 編集したらそのセルのエラー（赤）を消して、変更セル（黄）の表示に戻す
    grid.addEventListener("afteredit", (event) => {
      this.onAfterEdit(event.detail);
    });

    // 画面幅に合わせて列幅を埋める（レイアウト確定後にもう一度計算 + リサイズ追従）
    window.addEventListener("resize", this.onResize);
    this.$nextTick(() => {
      this.onResize();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    buildRows() {
      // promotionList からグリッド行を作る（promotionId は裏で保持し、保存時にどの行の更新かを送る）
      this.originalByPromotionId = {};
      let rows = [];
      for (const promotionData of this.promotionList) {
        // この行の ASP が「報酬額をプログラム情報から使う」なら報酬編集可、そうでなければ編集不可（グレー）
        const aspData = this.$store.getters.aspDataFromAllAspList(promotionData.aspId);
        let usesReward = false;
        if (aspData !== undefined && aspData.usesPromotionReward === true) {
          usesReward = true;
        } else {
          // no action.
        }

        //
        rows.push({
          promotionId: promotionData.promotionId,
          aspId: promotionData.aspId,
          aspName: promotionData.aspName,
          promotionName: promotionData.promotionName,
          aspPromotionName: promotionData.aspPromotionName,
          aspPromotionId: promotionData.aspPromotionId,
          cvName: promotionData.cvName,
          approvalRate: promotionData.approvalRate,
          reward: promotionData.reward,
          usesReward: usesReward,
        });

        // 変更検知用に元の値（承認率・報酬）を控えておく
        this.originalByPromotionId[String(promotionData.promotionId)] = {
          approvalRate: this.toText(promotionData.approvalRate),
          reward: this.toText(promotionData.reward),
        };
      }
      return rows;
    },
    toText(value) {
      let text = "";
      if (value !== undefined && value !== null) {
        text = String(value);
      } else {
        // no action.
      }
      return text;
    },
    computeColumnSizeData() {
      // 承認率・報酬額は固定幅のまま。状態・ASP・プログラムIDはやや広めの固定幅。
      // 残り幅を プログラム名・ASPプログラム名・オフラインCV名 の3列に割り振って右余白をなくす。
      const fixedState = 80;
      const fixedAspName = 200;
      const fixedAspPromotionId = 160;
      const fixedApprovalRate = 110;
      const fixedReward = 130;
      const scrollbarBuffer = 20;
      const minFlexible = 160;

      // コンテナ（コンポーネント全体）の横幅
      let containerWidth = 0;
      if (this.$el !== undefined && this.$el !== null && this.$el.clientWidth > 0) {
        containerWidth = this.$el.clientWidth;
      } else {
        // no action.
      }

      // 固定列を引いた残りを3等分（端数はプログラム名に寄せて余白を完全に埋める）
      const remaining = containerWidth - (fixedState + fixedAspName + fixedAspPromotionId + fixedApprovalRate + fixedReward) - scrollbarBuffer;
      let promotionNameSize = minFlexible;
      let aspPromotionNameSize = minFlexible;
      let cvNameSize = minFlexible;
      const flexBase = Math.floor(remaining / 3);
      if (flexBase >= minFlexible) {
        aspPromotionNameSize = flexBase;
        cvNameSize = flexBase;
        promotionNameSize = remaining - flexBase * 2;
      } else {
        // no action.（狭い画面では最小幅。横スクロールで対応）
      }

      //
      const sizeData = {
        state: fixedState,
        aspName: fixedAspName,
        promotionName: promotionNameSize,
        aspPromotionName: aspPromotionNameSize,
        aspPromotionId: fixedAspPromotionId,
        cvName: cvNameSize,
        approvalRate: fixedApprovalRate,
        reward: fixedReward,
      };
      return sizeData;
    },
    onResize() {
      // 画面幅が変わったら列幅を計算し直す（編集内容は source 側にあるので影響しない）
      const grid = this.$refs.grid;
      if (grid !== undefined) {
        grid.columns = this.buildColumns();
      } else {
        // no action.
      }
    },
    isCellChanged(model, field) {
      // 元の値と比べて変わっていれば true（変更セルの色付け用）
      const originalData = this.originalByPromotionId[String(model.promotionId)];
      if (originalData === undefined) {
        return false;
      } else {
        // no action.
      }
      return this.toText(model[field]) !== originalData[field];
    },
    isRowEdited(model) {
      // 承認率・報酬のどちらかが変更されていれば「編集済」
      let edited = false;
      if (this.isCellChanged(model, "approvalRate") === true || this.isCellChanged(model, "reward") === true) {
        edited = true;
      } else {
        // no action.
      }
      return edited;
    },
    buildColumns() {
      // 読み取り専用の目印列 + 編集列（承認率・報酬額）。報酬列は行ごとに readonly / グレーを切り替える
      const sizeData = this.computeColumnSizeData();
      let columns = [
        {
          prop: "promotionId",
          name: "状態",
          readonly: true,
          size: sizeData.state,
          cellTemplate: (createElement, props) => {
            // 編集された行は「編集済」、それ以外は「既存」
            let label = "既存";
            if (this.isRowEdited(props.model) === true) {
              label = "編集済";
            } else {
              // no action.
            }
            return label;
          },
          cellProperties: (props) => {
            // 編集済はオレンジ、既存はグレー
            let cellProps = undefined;
            if (this.isRowEdited(props.model) === true) {
              cellProps = { style: { backgroundColor: "#fdf6ec", color: "#e6a23c", fontWeight: "bold", textAlign: "center" } };
            } else {
              cellProps = { style: { color: "#909399", textAlign: "center" } };
            }
            return cellProps;
          },
        },
        { prop: "aspName", name: "ASP", readonly: true, size: sizeData.aspName },
        { prop: "promotionName", name: "プログラム名", readonly: true, size: sizeData.promotionName },
        { prop: "aspPromotionName", name: "ASPプログラム名", readonly: true, size: sizeData.aspPromotionName },
        { prop: "aspPromotionId", name: "プログラムID", readonly: true, size: sizeData.aspPromotionId },
        { prop: "cvName", name: "オフラインCV名", readonly: true, size: sizeData.cvName },
        {
          prop: "approvalRate",
          name: "承認率(%)",
          size: sizeData.approvalRate,
          cellProperties: (props) => {
            // エラーのセルは赤＋ツールチップ、変更されたセルは淡くハイライトする
            let cellProps = undefined;
            const errorMessage = this.getCellError(props.model, "approvalRate");
            if (errorMessage !== "") {
              cellProps = { style: { backgroundColor: "#fef0f0", border: "1px solid #f56c6c" } };
            } else if (this.isCellChanged(props.model, "approvalRate") === true) {
              cellProps = { style: { backgroundColor: "#fdf6ec" } };
            } else {
              // no action.
            }
            return cellProps;
          },
          cellTemplate: (createElement, props) => {
            // エラーがあれば値の横に赤い「！」アイコンを出す
            const value = this.toText(props.model.approvalRate);
            const errorMessage = this.getCellError(props.model, "approvalRate");
            if (errorMessage !== "") {
              return createElement("span", {}, [value, this.buildErrorIcon(createElement, errorMessage)]);
            } else {
              // no action.
            }
            return value;
          },
        },
        {
          prop: "reward",
          name: "報酬額(円)",
          size: sizeData.reward,
          readonly: (params) => {
            // 報酬額が不要な ASP の行は編集不可
            let result = false;
            if (params.model !== undefined && params.model.usesReward === false) {
              result = true;
            } else {
              // no action.
            }
            return result;
          },
          cellProperties: (props) => {
            // 報酬不要 ASP はグレー、エラーのセルは赤＋ツールチップ、変更されたセルは淡くハイライトする
            let cellProps = undefined;
            if (props.model !== undefined && props.model.usesReward === false) {
              cellProps = {
                style: {
                  backgroundColor: "#f2f2f2",
                  color: "#9a9a9a",
                },
              };
            } else {
              const errorMessage = this.getCellError(props.model, "reward");
              if (errorMessage !== "") {
                cellProps = { style: { backgroundColor: "#fef0f0", border: "1px solid #f56c6c" } };
              } else if (this.isCellChanged(props.model, "reward") === true) {
                cellProps = { style: { backgroundColor: "#fdf6ec" } };
              } else {
                // no action.
              }
            }
            return cellProps;
          },
          cellTemplate: (createElement, props) => {
            // 報酬額が不要な ASP の行は、数値ではなく「ASP自動」と表示する（裏の数値はそのまま保持）
            if (props.model !== undefined && props.model.usesReward === false) {
              return "ASP自動";
            } else {
              // no action.
            }

            // 通常の行は報酬額の数値をそのまま表示する
            let reward = "";
            if (props.model !== undefined && props.model.reward !== undefined && props.model.reward !== null) {
              reward = String(props.model.reward);
            } else {
              // no action.
            }

            // エラーがあれば値の横に赤い「！」アイコンを出す
            const errorMessage = this.getCellError(props.model, "reward");
            if (errorMessage !== "") {
              return createElement("span", {}, [reward, this.buildErrorIcon(createElement, errorMessage)]);
            } else {
              // no action.
            }
            return reward;
          },
        },
      ];
      return columns;
    },
    buildErrorIcon(createElement, message) {
      // 赤い丸に「！」のエラーアイコン。ホバーで即時に自前ツールチップを出す（ネイティブ title の遅延回避）
      return createElement(
        "span",
        {
          ref: (el) => {
            if (el !== undefined && el !== null) {
              el.onmouseenter = (event) => this.showTooltip(event, message);
              el.onmouseleave = () => this.hideTooltip();
            } else {
              // no action.
            }
          },
          style: {
            display: "inline-block",
            marginLeft: "6px",
            width: "16px",
            height: "16px",
            lineHeight: "16px",
            textAlign: "center",
            borderRadius: "50%",
            backgroundColor: "#f56c6c",
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: "bold",
            cursor: "pointer",
          },
        },
        "!"
      );
    },
    showTooltip(event, message) {
      // カーソル位置のすぐ近くに即時表示する
      this.tooltip.message = message;
      this.tooltip.x = event.clientX + 12;
      this.tooltip.y = event.clientY + 12;
      this.tooltip.visible = true;
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    onAfterEdit(detail) {
      // 編集されたセルのエラー（赤）を消す → 変更セル（黄）の表示に戻る
      if (detail === undefined || detail === null) {
        return;
      } else {
        // no action.
      }

      if (detail.model !== undefined && detail.model !== null && detail.prop !== undefined) {
        // 単一セル編集
        this.clearCellError(detail.model.promotionId, detail.prop);
      } else {
        // 範囲編集（コピペ・オートフィル）はまとめてエラーを消す
        this.errorByPromotionId = {};
      }

      //
      this.refreshColumns();
    },
    clearCellError(promotionId, field) {
      // 指定セルのエラーを取り除く（refreshColumns で再描画されるので reactivity は不要）
      const rowErrorData = this.errorByPromotionId[String(promotionId)];
      if (rowErrorData !== undefined && rowErrorData[field] !== undefined) {
        delete rowErrorData[field];
      } else {
        // no action.
      }
    },
    applyFilter() {
      const grid = this.$refs.grid;
      if (grid === undefined) {
        return;
      }

      // 検索語に一致しない行を trimmedRows で非表示にする（データは保持されるので編集・保存に影響しない）
      const searchText = this.searchText;
      let trimmedRows = {};
      if (searchText !== "") {
        for (let index = 0; index < this.allRows.length; index++) {
          if (this.matchesSearch(this.allRows[index], searchText) === true) {
            // ok（表示する）
          } else {
            trimmedRows[index] = true;
          }
        }
      } else {
        // no action.（空なら全表示）
      }
      grid.trimmedRows = trimmedRows;
    },
    matchesSearch(rowData, searchText) {
      // ASP名・プログラム名・ASPプログラム名・プログラムID・オフラインCV名 のいずれかに含まれれば一致
      let hit = false;
      const targetList = [
        rowData.aspName,
        rowData.promotionName,
        rowData.aspPromotionName,
        rowData.aspPromotionId,
        rowData.cvName,
      ];
      for (const target of targetList) {
        let text = "";
        if (target !== undefined && target !== null) {
          text = String(target);
        } else {
          // no action.
        }
        if (text.includes(searchText) === true) {
          hit = true;
        } else {
          // no action.
        }
      }
      return hit;
    },
    update() {
      const grid = this.$refs.grid;
      if (grid === undefined) {
        alert(this.apiErrorMessage);
        return;
      }

      // RevoGrid の現在値を取得してから検証・送信する
      grid.getSource().then((source) => {
        // 入力チェック。エラーは「セル単位（赤＋ツールチップ）」と「プログラム別の一覧（補助）」の両方を組み立てる
        let errorByPromotionId = {};
        let messageLineList = [];
        for (const rowData of source) {
          const rowErrorList = this.getRowErrorList(rowData);
          if (rowErrorList.length > 0) {
            const promotionId = String(rowData.promotionId);
            errorByPromotionId[promotionId] = {};

            let promotionName = this.toText(rowData.promotionName);
            if (promotionName === "") {
              promotionName = "（プログラム名不明）";
            } else {
              // no action.
            }

            //
            messageLineList.push("■ 「" + promotionName + "」");
            for (const errorData of rowErrorList) {
              errorByPromotionId[promotionId][errorData.field] = errorData.message;
              messageLineList.push("・" + errorData.label + "：" + errorData.message);
            }
          } else {
            // no action.
          }
        }

        // エラーセルの赤ハイライト＋ツールチップを反映する（列を作り直して再描画）
        this.errorByPromotionId = errorByPromotionId;
        this.refreshColumns();

        // 補助としてプログラム別の一覧も出す
        if (messageLineList.length > 0) {
          alert("入力内容にエラーがあります。\n\n" + messageLineList.join("\n"));
          return;
        } else {
          // no action.
        }

        //
        this.postBulkUpdate(source);
      });
    },
    refreshColumns() {
      // cellProperties を再評価させるために列定義を作り直す（source は変えないので編集内容は保持される）
      const grid = this.$refs.grid;
      if (grid !== undefined) {
        grid.columns = this.buildColumns();
      } else {
        // no action.
      }
    },
    getCellError(model, field) {
      // そのセルのエラーメッセージを返す（無ければ空文字）
      const rowErrorData = this.errorByPromotionId[String(model.promotionId)];
      if (rowErrorData === undefined) {
        return "";
      } else {
        // no action.
      }
      let message = "";
      if (rowErrorData[field] !== undefined && rowErrorData[field] !== null) {
        message = rowErrorData[field];
      } else {
        // no action.
      }
      return message;
    },
    getRowErrorList(rowData) {
      // 1行分の検証。誤りのある項目だけ {label, message} で返す
      let errorList = [];

      // 承認率（1〜1000の整数）
      const approvalRate = this.toText(rowData.approvalRate);
      let isValidApprovalRate = false;
      if (/^[0-9]+$/.test(approvalRate) === true) {
        const approvalRateNumber = Number(approvalRate);
        if (approvalRateNumber >= 1 && approvalRateNumber <= 1000) {
          isValidApprovalRate = true;
        } else {
          // no action.
        }
      } else {
        // no action.
      }
      if (isValidApprovalRate === true) {
        // ok.
      } else {
        errorList.push({ field: "approvalRate", label: "承認率", message: "1〜1000の整数で入力してください。" });
      }

      // 報酬額（編集可の行のみ 0以上の整数をチェック。編集不可(グレー)の行は既存値のままなのでチェック不要）
      if (rowData.usesReward === true) {
        const reward = this.toText(rowData.reward);
        if (/^[0-9]+$/.test(reward) === true) {
          // ok.
        } else {
          errorList.push({ field: "reward", label: "報酬額", message: "0以上の整数で入力してください。" });
        }
      } else {
        // no action.
      }

      //
      return errorList;
    },
    postBulkUpdate(source) {
      // promotion_list を組み立てる（サーバが読むキーは snake_case）
      let promotionList = [];
      for (const rowData of source) {
        promotionList.push({
          promotion_id: String(rowData.promotionId),
          reward: Number(rowData.reward),
          approval_rate: Number(rowData.approvalRate),
        });
      }

      //
      this.isUpdating = true;
      let params = new URLSearchParams();
      params.append("promotion_list", JSON.stringify(promotionList));
      this.$axios
        .post(`${this.rt}/l/api/tools02/promotion/bulk-update`, params)
        .then((response) => {
          console.log(response);
          this.isUpdating = false;
          if (response.data.status === 200) {
            alert("一括更新が完了しました。");
            this.$emit("saved");
          } else if (response.data.status === 401) {
            alert(response.data.message);
            this.$router.push("/login");
          } else if (response.data.status === 400) {
            alert("入力値に誤りがあります。内容を確認してください。");
          } else {
            alert(this.apiErrorMessage);
          }
        })
        .catch((error) => {
          console.log(error);
          this.isUpdating = false;
          alert(this.apiErrorMessage);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.tools02-promotions-promotion-bulk-editor {
  .description {
    font-size: 13px;
    color: #666;
    line-height: 1.8;
    margin-bottom: 12px;
  }

  .search-row {
    margin-bottom: 12px;
  }

  .grid {
    height: 70vh;
    min-height: 480px;
  }

  .actions {
    margin-top: 16px;
  }
}

.cell-error-tooltip {
  position: fixed;
  z-index: 3000;
  max-width: 280px;
  padding: 6px 10px;
  background-color: #303133;
  color: #fff;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  white-space: normal;
}
</style>
